import { PlusIcon } from "@heroicons/react/outline";
import { Link } from "react-router-dom";
import { OAuthError, postData } from "../backend";
import { Dispatch, SetStateAction, useCallback, useEffect, useRef } from "react";
import ErrorModal from "./ErrorModal";
import OAuthModal from "./OAuthModal";
import { useModal } from "../contexts";
import { useTranslation } from 'react-i18next';
import posthog from "posthog-js";


type Props = {
  onFinish: () => void;
  setLoading: Dispatch<SetStateAction<boolean>>;
}

function ConversionGoogleDrive(props: Props) {
  const { setLoading, onFinish } = props;
  const { t } = useTranslation();
  const { openModal } = useModal();

  // Guard against onFinish firing more than once (on-mount initialize, the
  // OAuth focus refresh and repeated Connect clicks can all resolve). Advancing
  // the onboarding step twice pushes `step` past the end of the steps array.
  const hasFinished = useRef(false);

  const finish = useCallback(() => {
    if (hasFinished.current) return;
    hasFinished.current = true;
    onFinish();
  }, [onFinish]);

  const initialize = useCallback(async () => {
    try {
      await postData("/auth/access-token/", { scopes: ["https://www.googleapis.com/auth/drive"] });
      finish();
    } catch (error) { }
  }, [finish]);

  const handleConnectGoogle = async () => {
    posthog.capture("onboarding_connect_google_drive_clicked");
    setLoading(true);
    try {
      await postData("/auth/access-token/", { scopes: ["https://www.googleapis.com/auth/drive"] });
      setLoading(false);
      finish();
    } catch (error) {
      setLoading(false);
      if (error instanceof OAuthError)
        // Open the OAuth flow from the modal so window.open is triggered by a
        // direct user click rather than after an await, otherwise the browser
        // silently blocks it as a popup and the Connect button looks dead. When
        // focus returns to the tab the modal re-checks Drive access via onClose.
        openModal(<OAuthModal oauthUrl={error.oauthUrl} onClose={initialize} />);
      else
        openModal(<ErrorModal details={error} />);
    }
  }

  useEffect(() => {
    initialize();
  }, [initialize]);

  return (
    <>
      <div>
        <h2 className="font-semibold text-gray-600 text-4xl text-center">
          {t("Connect Google Drive to Portant")}
        </h2>
        <p className="mt-2 text-gray-400 text-center font-semibold">
          {t("Allow Portant to access files in your Google Drive")} <br />
          {t("so we can automatically create the required documents")}
        </p>
      </div>

      <div className="flex justify-center gap-16 items-center">
        <img
          className='h-12 w-12'
          src={`${process.env.REACT_APP_STATIC_URL}/drive-icon.svg`}
          alt='Google Drive Logo'
        />
        <PlusIcon className="h-7 w-7 text-gray-500" />
        <img
          className='h-12 w-12'
          src={`${process.env.REACT_APP_STATIC_URL}/logo-mark-blue.svg`}
          alt='Portant Logo Small'
        />
      </div>

      <div className="flex justify-center gap-48 items-center w-full">
        <Link to="/" onClick={() => posthog.capture("onboarding_connect_google_drive_cancelled")}>
          <button className="btn btn-gray w-32">
            {t("Cancel")}
          </button>
        </Link>
        <button className={`btn btn-blue w-32`} onClick={handleConnectGoogle}>
          {t("Connect")}
        </button>
      </div>
    </>
  );
}

export default ConversionGoogleDrive;
